import type { ReactNode } from 'react';
import { Carousel, CarouselItem } from '@worldcoo/ui/components/carousel';
import { Skeleton } from '@worldcoo/ui/components/skeleton';
import { cn } from '@worldcoo/ui/lib/utils';

/** Matches the page's horizontal padding, so slides line up with the text above. */
const galleryInset = 'max(var(--gallery-inset), calc((100% - 1366px) / 2 + var(--gallery-inset)))';
const gallerySpacing = 'gap-4 [--gallery-inset:1.25rem] sm:gap-5 sm:[--gallery-inset:2.5rem]';
const galleryPadding = { paddingLeft: galleryInset, paddingRight: galleryInset };
const slideWidth = 'w-full basis-[calc(100%-2.5rem)] sm:basis-[calc((100%-1.5rem)/2)] lg:basis-[calc((100%-2.5rem)/3)]';
const slideAspectRatio = 'aspect-[413/286]';

export default function GalleryContent(props: {
	images: { key: string; alt: string; image: ReactNode; mobileOnly?: boolean }[];
}) {
	const hasMultipleImages = props.images.length > 1;

	return (
		<section className="w-full pb-4 sm:pb-6">
			<style>{`@media (min-width: 1024px) { .gallery-carousel::scroll-button(*) { display: none; } }`}</style>
			<Carousel className={cn('gallery-carousel', gallerySpacing)} style={galleryPadding}>
				{props.images.map((item) => (
					<CarouselItem
						key={item.key}
						className={cn(
							slideWidth,
							// Lazy slides are blank until decoded.
							'bg-secondary rounded-2xl',
							// A sliver of the next slide hints the carousel scrolls.
							!hasMultipleImages && 'basis-full',
							// Duplicated to gain vertical space on mobile; from lg up it is in CampaignHero.
							item.mobileOnly && 'lg:hidden',
						)}
					>
						{item.image}
					</CarouselItem>
				))}
			</Carousel>
		</section>
	);
}

/** No `Carousel` yet, but the real slide widths and ratio, so images drop in without a layout shift. */
export function GallerySkeleton() {
	return (
		<section className="w-full pb-4 sm:pb-6">
			<div className={cn('flex overflow-hidden', gallerySpacing)} style={galleryPadding}>
				{[0, 1, 2].map((index) => (
					<Skeleton key={index} className={cn('bg-secondary shrink-0 rounded-2xl', slideWidth, slideAspectRatio)} />
				))}
			</div>
		</section>
	);
}
